import { ComponentType } from "react";
import RobotOutlineIcon from "@src/lib/icons/18px/robot-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import VideoOutlineIcon from "@src/lib/icons/18px/video-outline";
import MapOutlineIcon from "@src/lib/icons/18px/map-outline";
import GridLayoutCols3Icon from "@src/lib/icons/18px/grid-layout-cols-3";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import ClipboardCheckIcon from "@src/lib/icons/18px/clipboard-check";
import { IconProps } from "@src/lib/icons/types";
import { Ff, UserType } from "@src/types.generated";

// Monthly releases surfaced at the top of the Admin and Manager homepages.
// Release slugs, copy, and assets mirror the release pages in the `changelog`
// repo (src/lib/releases.ts) so links resolve and the two read alike.

export const RELEASES_BASE_URL = "https://changelog.opus.so/releases";
export const HELP_CENTER_URL = "https://help.opus.so/en/";

type ReleaseAudience = UserType.Admin | UserType.Manager;

// How much room a feature gets, mirroring the changelog release page:
// - "lead": the headline feature, selected when the letter opens.
// - "companion": shown alongside the lead (e.g. Opus MCP next to Agents).
// - "more": listed by name under "Also new" on the homepage card.
export type ReleaseFeatureTier = "lead" | "companion" | "more";

export type ReleaseHeadlineFeature = {
  slug: string;
  title: string;
  // One short line about the feature, from the changelog release page.
  deck: string;
  icon: ComponentType<IconProps>;
  tier: ReleaseFeatureTier;
  // The feature's clip from the changelog release page, shown in the letter
  // when the feature is selected: asset URL without extension (it serves
  // `.mp4` and a `.jpg` poster), with the video's size so the layout doesn't
  // jump as it loads.
  video?: { url: string; width: number; height: number };
  // Logos of the tools a companion works with.
  logos?: { name: string; src: string }[];
  // Who sees the feature; defaults to Admins and Managers.
  audience?: ReleaseAudience[];
};

// How the release is introduced on the homepage.
// - "card": an inline headline card at the top of the homepage.
// - "envelope": a sealed envelope pops up the first time; opening it reveals
//   the announcement as a letter, and later visits show the inline card.
export type ReleaseExperience = "card" | "envelope";

export type ReleaseHeadlineRelease = {
  slug: string;
  month: string;
  headline: string;
  // One line under the headline, per role.
  subline: Record<ReleaseAudience, string>;
  // Local-time window during which the headline shows on the homepage.
  startsAt: string;
  endsAt: string;
  experience: ReleaseExperience;
  // When set, only viewers with this flag on see the release (previews
  // ignore it), so it can roll out business by business.
  flag?: Ff;
  features: ReleaseHeadlineFeature[];
};

// Newest first. The first release whose window contains today is shown.
export const RELEASES: ReleaseHeadlineRelease[] = [
  {
    slug: "2026-10",
    month: "October",
    headline: "Put Opus to work with agents",
    subline: {
      [UserType.Admin]:
        "Automate the tedious work so that you can focus on what’s high impact.",
      [UserType.Manager]:
        "See how your training is doing on one page, grade Audits your way, and ask Claude or ChatGPT about your team’s training.",
    },
    startsAt: "2026-10-01T00:00:00",
    endsAt: "2026-11-01T00:00:00",
    experience: "envelope",
    flag: Ff.OctoberReleaseEnvelope,
    features: [
      {
        slug: "opus-agents",
        title: "Opus Agents",
        deck: "Build agents that know your team and get work done for you.",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/agents`,
          width: 1100,
          height: 688,
        },
        icon: RobotOutlineIcon,
        tier: "lead",
        audience: [UserType.Admin],
      },
      {
        slug: "opus-mcp",
        title: "Opus MCP",
        deck: "Integrate with your AI tool",
        logos: [
          {
            name: "Claude",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/claude.svg`,
          },
          {
            name: "ChatGPT",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/openai.svg`,
          },
          {
            name: "Gemini",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/gemini.svg`,
          },
          {
            name: "Copilot",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/copilot.svg`,
          },
        ],
        icon: LinkIcon,
        tier: "companion",
      },
      {
        slug: "video-editor",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/video-editor`,
          width: 1400,
          height: 1080,
        },
        title: "Video Editor",
        deck: "Trim clips, add a voiceover or an AI voice, and add music, then save it in place.",
        icon: VideoOutlineIcon,
        tier: "more",
      },
      {
        slug: "improved-path-builder",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/path`,
          width: 1400,
          height: 1080,
        },
        title: "Improved Path Builder",
        deck: "Keep changes in draft until you publish, and see who's on each step in the new Runs tab.",
        icon: MapOutlineIcon,
        tier: "more",
      },
      {
        slug: "content-detail-pages",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/detail`,
          width: 1024,
          height: 768,
        },
        title: "Content Overview",
        deck: "A new Overview shows results, who has it, and a phone preview of what your team sees.",
        icon: GridLayoutCols3Icon,
        tier: "more",
      },
      {
        slug: "profile-photos",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/profile`,
          width: 1436,
          height: 1080,
        },
        title: "Profile Photos",
        deck: "Let your team personalize their profiles, and recognize each other more easily.",
        icon: UserOutlineIcon,
        tier: "more",
      },
      {
        slug: "audit-grades",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/audit`,
          width: 1400,
          height: 1080,
        },
        title: "Audit Grades",
        deck: "Grade every Audit and each section from Excellent to Needs improvement, using ranges you set.",
        icon: ClipboardCheckIcon,
        tier: "more",
      },
    ],
  },
];

export const releaseUrl = (release: ReleaseHeadlineRelease): string =>
  `${RELEASES_BASE_URL}/${release.slug}/`;

export const featuresFor = (
  release: ReleaseHeadlineRelease,
  userType: ReleaseAudience,
): ReleaseHeadlineFeature[] =>
  release.features.filter(
    (feature) => !feature.audience || feature.audience.includes(userType),
  );

export const getActiveRelease = (
  now: Date,
  previewSlug?: string,
): ReleaseHeadlineRelease | null => {
  if (previewSlug) {
    return RELEASES.find((release) => release.slug === previewSlug) ?? null;
  }
  return (
    RELEASES.find(
      (release) =>
        now >= new Date(release.startsAt) && now < new Date(release.endsAt),
    ) ?? null
  );
};
